import { useCallback, useMemo, useRef } from 'react';
import { parseISO } from 'date-fns';
import isEqual from 'lodash/isEqual';

// Helper function to normalize location names
const normalizeLocationName = (name) => {
  if (!name) return '';
  return name.toLowerCase().replace(/[-\s']+/g, ' ').trim();
};

export function useEventFilters(selectedEventTypes, selectedRegion, selectedTimeRange, selectedActorType, selectedEventCategories) {
  // Store previous filter results to avoid unnecessary recalculations
  const previousParams = useRef({ 
    selectedEventTypes: null, 
    selectedRegion: null, 
    selectedTimeRange: null, 
    selectedActorType: null, 
    selectedEventCategories: null 
  });
  const cachedResults = useRef({
    filteredAcledEvents: null,
    filteredTimeChartData: null
  });

  // Memoize the filter functions to prevent unnecessary recreations
  const filterFunctions = useMemo(() => {
    const typeFilter = event => selectedEventTypes.length === 0 || selectedEventTypes.includes(event.event_type);
    
    const locationFilter = event => {
      if (!selectedRegion) return true;
      const normalizedEventAdmin1 = normalizeLocationName(event.standardized_admin1 || event.admin1);
      const normalizedSelectedAdmin1 = normalizeLocationName(selectedRegion.name);
      return normalizedEventAdmin1 === normalizedSelectedAdmin1;
    };
    
    const timeFilter = event => {
      const eventDate = parseISO(event.event_date).getTime();
      return eventDate >= selectedTimeRange[0] && eventDate <= selectedTimeRange[1];
    };
    
    const actorFilter = event => !selectedActorType || 
      event.actor1 === selectedActorType || 
      event.actor2 === selectedActorType ||
      event.inter1 === selectedActorType ||  
      event.inter2 === selectedActorType;
    
    const categoryFilter = event => selectedEventCategories.length === 0 || 
      selectedEventCategories.includes(event.inter1) || 
      selectedEventCategories.includes(event.inter2);
    
    return {
      typeFilter,
      locationFilter,
      timeFilter,
      actorFilter,
      categoryFilter
    };
  }, [selectedEventTypes, selectedRegion, selectedTimeRange, selectedActorType, selectedEventCategories]);

  const filterAcledEvents = useCallback(events => {
    if (!events || !Array.isArray(events)) return [];

    // Check if filter params have changed
    const currentParams = {
      selectedEventTypes,
      selectedRegion,
      selectedTimeRange,
      selectedActorType,
      selectedEventCategories
    };
    
    // Use cached results if inputs haven't changed
    if (cachedResults.current.filteredAcledEvents && 
        isEqual(currentParams, previousParams.current) && 
        cachedResults.current.events === events) {
      return cachedResults.current.filteredAcledEvents;
    }
    
    // Update previous params reference
    previousParams.current = currentParams;
    cachedResults.current.events = events;

    const { typeFilter, locationFilter, timeFilter, actorFilter, categoryFilter } = filterFunctions;

    // First apply all the regular filters
    const filteredEvents = events.filter(event => {
      if (!event) return false;
      return typeFilter(event) && 
             locationFilter(event) && 
             timeFilter(event) && 
             actorFilter(event) && 
             categoryFilter(event);
    });

    // Now ensure all required fields are present for interaction chart and only include valid interactions
    const result = filteredEvents
      .filter(event => {
        // Only include events that have both actor1 and actor2
        return Boolean(event.actor1) && Boolean(event.actor2) && 
               // And have interaction categories
               Boolean(event.inter1) && Boolean(event.inter2);
      })
      .map(event => ({
        ...event,
        actor1: event.actor1 || '',
        actor2: event.actor2 || '',
        inter1: event.inter1 || '',
        inter2: event.inter2 || '',
        fatalities: event.fatalities || 0
      }));
    
    // Cache the results
    cachedResults.current.filteredAcledEvents = result;
    
    return result;
  }, [filterFunctions, selectedEventTypes, selectedRegion, selectedTimeRange, selectedActorType, selectedEventCategories]);

  const filterTimeChartData = useCallback(events => {
    if (!events || !Array.isArray(events)) return [];

    // Check if filter params have changed (except time range since we want all data for the chart)
    const currentParams = {
      selectedEventTypes,
      selectedRegion,
      selectedActorType,
      selectedEventCategories
    };
    
    // Use cached results if inputs haven't changed
    if (cachedResults.current.filteredTimeChartData && 
        isEqual(currentParams, previousParams.current.timeChartParams) && 
        cachedResults.current.timeChartEvents === events) {
      return cachedResults.current.filteredTimeChartData;
    }
    
    // Update previous params reference for time chart
    previousParams.current.timeChartParams = currentParams;
    cachedResults.current.timeChartEvents = events;

    const { typeFilter, locationFilter, actorFilter, categoryFilter } = filterFunctions;

    const result = events.filter(event => {
      if (!event) return false;
      return typeFilter(event) && 
             locationFilter(event) && 
             actorFilter(event) && 
             categoryFilter(event);
    });
    
    // Cache the results
    cachedResults.current.filteredTimeChartData = result;
    
    return result;
  }, [filterFunctions, selectedEventTypes, selectedRegion, selectedActorType, selectedEventCategories]);

  return { filterAcledEvents, filterTimeChartData };
}